import { Button, Input, Modal, Radio, Select, Tooltip } from 'antd';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { CONFIG } from '../../../config/Config';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import { createMultiModel, deleteModel, getLatestAlgorithmVersion } from '../../../lib/api/algorithm-api';
import { deleteInference } from '../../../lib/api/aws-api';
import { createTraining } from '../../../lib/api/training-api';
import { useGetAlgorithm } from '../../../lib/hooks';
import { getLabelValue } from '../../../lib/util';
import { InferenceFile, Model, Training } from '../../../types';
import UploadMultiContainer from '../upload/UploadMultiContainer';
import { DatasetSelection } from './DatasetSelection';
import { getSecondToLatestDatasetVersion } from '../../../lib/api/dataset-api';

const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => any;
  onCreate: (model: Model, training: Training) => any;
  algorithmId: string;
};

enum Source {
  DATASET,
  UPLOAD,
}

const CreateMultiModel = (props: Props) => {
  const { visible, onClose, onCreate, algorithmId } = props;

  const { algorithm } = useGetAlgorithm(algorithmId);
  const [annotationTypes, setAnnotationTypes] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [inferenceList, setInferenceList] = useState<InferenceFile[]>([]);
  const [model, setModel] = useState<null | Model>(null);
  const [nameValue, setNameValue] = useState<string>('');
  const [datasetIdValue, setDatasetIdValue] = useState<string | null>(null);
  const [flowMode, setFlowMode] = useState<string>('');
  const [flowName, setFlowName] = useState<null | string>(null);
  const [step, setStep] = useState<0 | 1>(0);
  const [source, setSource] = useState<Source>(Source.UPLOAD);

  const nextEnabled =
    source === Source.UPLOAD
      ? nameValue && flowMode && flowName && selectedLabels.length > 0
      : nameValue && datasetIdValue;
  const submitEnabled =
    inferenceList &&
    inferenceList.length > 0 &&
    inferenceList.every(inference => ['FINISHED', 'ERROR'].includes(inference.status));

  const nextButtonMessage =
    source === Source.UPLOAD
      ? 'Enter a name and select at least 1 label before continuing.'
      : 'Enter a name and select a dataset before continuing.';

  const rollbackCreation = useCallback(async () => {
    setModel(null);
    setInferenceList([]);
    if (!model) return;
    try {
      await deleteModel(model.id);
      await Promise.all(inferenceList.map(inference => deleteInference(inference.id)));
    } catch (e) {
      console.log(e);
    }
  }, [model, inferenceList]);

  useEffect(() => {
    if (algorithm) {
      setAnnotationTypes(algorithm.info.annotationTypes);
    }
    return () => {
      setAnnotationTypes([]);
    };
  }, [algorithm]);

  const onNameChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setNameValue(event.currentTarget.value);
  };

  const onLabelChange = (value: string[]) => {
    setSelectedLabels(value);
  };

  const goToUpload = async () => {
    const version = await getLatestAlgorithmVersion(algorithmId);
    console.log('latest version has id ' + version.id);
    const model = await createMultiModel(
      nameValue,
      flowName,
      flowMode,
      algorithmId,
      version.id,
      selectedLabels,
      datasetIdValue
    );
    setModel(model);

    if (source === Source.DATASET && model.datasetId) {
      const datasetVersion = await getSecondToLatestDatasetVersion(model.datasetId.toString());
      const training = await createTraining(
        model.id,
        inferenceList.map(inference => inference.id),
        datasetVersion.version_id
      );
      onModalClose(true);
      onCreate(model, training);
    } else {
      setStep(1);
    }
  };

  const onSubmit = async () => {
    if (!model) return;

    const training = await createTraining(
      model.id,
      inferenceList.map(inference => inference.id),
      'Version 1.0'
    );
    onModalClose(false);
    onCreate(model, training);
  };

  const onModalClose = async (remove: boolean) => {
    if (remove && model) {
      await rollbackCreation();
    }

    onClose();

    algorithm && setSelectedLabels([]);
    setNameValue('');
    setStep(0);
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>New model</ModalTitle>}
      onCancel={() => onModalClose(true)}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={nextEnabled ? '' : nextButtonMessage}>
              <Button style={{ marginLeft: '10px' }} type="primary" onClick={goToUpload} disabled={!nextEnabled}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={submitEnabled ? '' : 'Upload at least 1 document and wait for them to finish analysing.'}>
              <Button type="primary" onClick={onSubmit} style={{ marginLeft: '10px' }} disabled={!submitEnabled}>
                Review
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 || (source === Source.DATASET && step === 1) ? (
        <div>
          <FormItemLabel>This process will create a new model.</FormItemLabel>
          <Input placeholder="Name" value={nameValue} onChange={onNameChange} style={{ margin: '0px 0px 20px 0px' }} />
          <div className="mb-6">
            <FormItemLabel>New model based on</FormItemLabel>
            <Radio.Group defaultValue={source} buttonStyle="solid" onChange={value => setSource(value.target.value)}>
              <Radio.Button value={Source.UPLOAD}>Labels and uploads</Radio.Button>
              <Radio.Button value={Source.DATASET}>Existing dataset</Radio.Button>
            </Radio.Group>
          </div>
          {source === Source.UPLOAD && (
            <>
              <ModalSubTitle>Labels</ModalSubTitle>
              <FormItemLabel>Select the labels you’d like included in this model.</FormItemLabel>
              <Select
                value={selectedLabels}
                mode="tags"
                style={{ width: '100%', margin: '0px 0px 20px 0px' }}
                placeholder="Labels"
                onChange={onLabelChange}>
                {annotationTypes.map(annotation => (
                  <Option key={annotation} value={annotation}>
                    {getLabelValue(annotation)}
                  </Option>
                ))}
              </Select>
            </>
          )}
          {source === Source.DATASET && (
            <>
              <ModalSubTitle>Dataset</ModalSubTitle>
              <FormItemLabel>Select a dataset for the new model</FormItemLabel>
              <DatasetSelection setDatasetId={setDatasetIdValue} datasetId={datasetIdValue} />
            </>
          )}
          <ModalSubTitle>Other info</ModalSubTitle>
          <FormItemLabel>Flow name</FormItemLabel>
          {CONFIG.multiflow.definitions && (
            <Select
              style={{ minWidth: 300, margin: '0px 0px 20px 0px' }}
              value={flowName || undefined}
              onChange={(value: string) => setFlowName(value)}>
              {CONFIG.multiflow.definitions.map(def => (
                <Option key={def.name} value={def.name}>
                  {def.name}
                </Option>
              ))}
            </Select>
          )}
          <FormItemLabel>Mode</FormItemLabel>
          <Select value={flowMode} onChange={setFlowMode} style={{ width: '100%', margin: '0px 0px 20px 0px' }}>
            <Option key="layout" value="layout">
              Layout
            </Option>
            <Option key="vat" value="VAT">
              VAT
            </Option>
            <Option key="supplierName" value="supplierName">
              Supplier Name
            </Option>
            <Option key="always" value="always">
              Run Always
            </Option>
          </Select>
        </div>
      ) : (
        <div>
          {model && (
            <div>
              <FormItemLabel>
                To create a custom model, we need some example documents. We advice you to upload and review a minimum
                of 3 documents.
              </FormItemLabel>
              <UploadMultiContainer
                flowName={flowName || ''}
                modelId={model.id}
                inferenceList={inferenceList}
                onInferenceListUpdated={inferenceList => setInferenceList(inferenceList)}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateMultiModel;
